import { useRef } from "react";
import { ArrowLeft, ArrowRight, Quote } from "lucide-react";

const CASES = [
  { company: "Global CPG Leader", sub: "Reinventing the month-end close", quote: "Agents reconcile millions of transactions across 12 ERPs and surface only the exceptions that need a human.", m: [["45%", "Faster Close"], ["12", "ERPs Unified"]], tags: ["Reconciliation", "Finance"] },
  { company: "Fortune 100 Bank", sub: "Trusted context for finance ops", quote: "A shared semantic layer means every team and agent works from the same governed numbers.", m: [["3\u00D7", "Faster Recon"], ["100%", "Governed"]], tags: ["Semantic Layer", "BFSI"] },
  { company: "Industrial Manufacturer", sub: "Smarter demand & supply planning", quote: "Agentic planning balances service levels and working capital across a global network in real time.", m: [["20%", "Lower Inventory"], ["Live", "In Production"]], tags: ["Planning", "Supply Chain"] },
  { company: "Global Retail Group", sub: "Campaign orchestration at scale", quote: "Planning, content and activation are coordinated end-to-end \u2014 weeks of work now happen in hours.", m: [["2\u00D7", "Throughput"], ["Live", "Channels"]], tags: ["Marketing", "Content"] },
];

const mono = (n) => n.split(" ").map((w) => w[0]).slice(0, 2).join("");

/* C · Monogram logo header + metric bar footer */
const CardC = ({ c }) => (
  <div className="shrink-0 w-[300px] sm:w-[340px] snap-start rounded-2xl bg-white border border-[#3E2818]/10 overflow-hidden flex flex-col">
    <div className="p-6 flex-1">
      <div className="flex items-center gap-3">
        <span className="w-11 h-11 rounded-xl bg-[#211307] text-[#E8AA68] flex items-center justify-center font-display font-medium text-[15px]">{mono(c.company)}</span>
        <div><p className="font-display text-lg font-medium text-[#3E2818] leading-tight">{c.company}</p><p className="text-[11.5px] text-[#3E2818]/55">{c.sub}</p></div>
      </div>
      <p className="text-[13.5px] text-[#3E2818]/70 leading-relaxed mt-4">{c.quote}</p>
      <div className="mt-4 flex flex-wrap gap-2">{c.tags.map((t) => <span key={t} className="text-[11px] font-semibold rounded-md px-2.5 py-1 bg-[#8A5730]/10 text-[#8A5730]">{t}</span>)}</div>
    </div>
    <div className="grid grid-cols-2 divide-x divide-[#3E2818]/10 border-t border-[#3E2818]/10 bg-[#FBF8F2]">
      {c.m.map(([n, l]) => <div key={l} className="p-4 text-center"><div className="font-display text-2xl font-medium text-[#8A5730]">{n}</div><div className="text-[10.5px] uppercase tracking-[0.12em] text-[#3E2818]/50 mt-0.5">{l}</div></div>)}
    </div>
  </div>
);

/* D · Quote-forward testimonial */
const CardD = ({ c }) => (
  <div className="shrink-0 w-[300px] sm:w-[360px] snap-start rounded-2xl bg-gradient-to-b from-[#FBF2E1] to-white border border-[#8A5730]/15 p-6 flex flex-col">
    <Quote size={26} className="text-[#8A5730]/40" />
    <p className="font-display text-lg font-medium text-[#3E2818] leading-snug mt-3 flex-1">{c.quote}</p>
    <div className="mt-5 flex gap-6">
      {c.m.map(([n, l]) => <div key={l}><div className="font-display text-2xl font-medium text-[#8A5730]">{n}</div><div className="text-[10.5px] uppercase tracking-[0.12em] text-[#3E2818]/50 mt-0.5">{l}</div></div>)}
    </div>
    <div className="h-px my-4 bg-[#3E2818]/10" />
    <p className="font-display text-[15px] font-medium text-[#3E2818]">{c.company}</p>
    <p className="text-[12px] text-[#3E2818]/55">{c.sub}</p>
  </div>
);

/* E · Metric-hero on top */
const CardE = ({ c }) => (
  <div className="shrink-0 w-[300px] sm:w-[340px] snap-start rounded-2xl bg-white border border-[#3E2818]/10 flex flex-col overflow-hidden">
    <div className="bg-[#211307] p-6">
      <div className="font-display text-5xl font-medium text-[#E8AA68] leading-none">{c.m[0][0]}</div>
      <div className="text-[11px] uppercase tracking-[0.14em] text-white/70 mt-2">{c.m[0][1]}</div>
    </div>
    <div className="p-6 flex-1 flex flex-col">
      <p className="font-display text-lg font-medium text-[#3E2818]">{c.company}</p>
      <p className="text-[11.5px] text-[#3E2818]/55">{c.sub}</p>
      <p className="text-[13px] text-[#3E2818]/70 leading-relaxed mt-3 flex-1">{c.quote}</p>
      <div className="mt-4 flex flex-wrap gap-2">{c.tags.map((t) => <span key={t} className="text-[11px] font-semibold rounded-md px-2.5 py-1 bg-[#8A5730]/10 text-[#8A5730]">{t}</span>)}</div>
    </div>
  </div>
);

const Carousel = ({ Card }) => {
  const ref = useRef(null);
  const scroll = (d) => ref.current?.scrollBy({ left: d * 360, behavior: "smooth" });
  return (
    <div>
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="font-mono2 text-[11px] uppercase tracking-[0.28em] text-[#8A5730] mb-4">Powering Enterprises of All Sizes</p>
          <h2 className="font-display text-3xl lg:text-[2.75rem] font-medium tracking-tight text-[#3E2818] leading-[1.05]">Agents <span className="italic">in production</span><br />not just demos.</h2>
        </div>
        <div className="hidden sm:flex gap-2">
          <button onClick={() => scroll(-1)} className="w-11 h-11 rounded-full border border-[#3E2818]/25 flex items-center justify-center text-[#3E2818] hover:bg-[#211307] hover:text-[#F7F5F0] hover:border-[#211307] transition-colors"><ArrowLeft size={17} /></button>
          <button onClick={() => scroll(1)} className="w-11 h-11 rounded-full border border-[#3E2818]/25 flex items-center justify-center text-[#3E2818] hover:bg-[#211307] hover:text-[#F7F5F0] hover:border-[#211307] transition-colors"><ArrowRight size={17} /></button>
        </div>
      </div>
      <div ref={ref} className="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-4 -mx-1 px-1 items-stretch [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {[...CASES, ...CASES].map((c, i) => <Card key={i} c={c} />)}
      </div>
    </div>
  );
};

const OPTIONS = [
  { id: "c", title: "Option C · Monogram Logo + Metric Bar", note: "Company monogram badge and name up top, quote and tags in the body, and a split metric bar anchored at the bottom. Logo-ready and very tidy.", Card: CardC },
  { id: "d", title: "Option D · Quote-Forward", note: "Leads with the customer quote (testimonial-first), metrics beneath, and the company signed at the bottom. Warm and trust-building.", Card: CardD },
  { id: "e", title: "Option E · Metric Hero", note: "A bold dark metric header caps each card, then company, quote and tags. The result number is the hero.", Card: CardE },
];

export default function CaseStudyOptions4() {
  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#8A5730]">Success Stories · More Carousel Templates</p>
        <h1 className="font-display text-2xl lg:text-3xl font-medium mt-2 tracking-tight text-[#3E2818]">More card styles (same carousel)</h1>
        <p className="text-sm text-[#3E2818]/60 mt-3 max-w-2xl leading-relaxed">Placeholder stories. Use the arrows or scroll. Tell me the letter (or mix with A/B from the previous set).</p>

        <div className="mt-12 space-y-20">
          {OPTIONS.map((o) => (
            <div key={o.id}>
              <p className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#B87A32] mb-6">{o.title}</p>
              <Carousel Card={o.Card} />
              <p className="text-[12px] text-[#3E2818]/55 mt-4 leading-snug max-w-3xl">{o.note}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
